import type { FilterCategory, ReportConfig, ReportType } from "./types";

// configuration for each report type - defines which filters are available
export const REPORT_CONFIGS: Record<ReportType, ReportConfig> = {
  INVOICE_LIST: {
    filters: [
      "report_type",
      "posted_dates",
      "service_dates",
      "clients",
      "service_types",
      "file_ids",
      "invoice_statuses",
      "payment_status",
      "invoice_numbers",
      "icn",
      "claim_id",
      "claim_number",
      "rre_name",
      "rre_id",
      "s111_claimant_name",
    ],
    customComponents: [],
  },
  CLIENT_LIST_REPORT: {
    filters: ["report_type", "clients"],
    customComponents: [],
  },
  FILE_LIST: {
    filters: ["report_type", "file_opened_dates", "clients"],
    customComponents: [],
  },
  INVOICE_LINE_ITEM_REPORT: {
    filters: ["report_type", "posted_dates", "invoice_statuses"],
    customComponents: [],
  },
  WORK_IN_PROGRESS: {
    filters: ["report_type", "service_dates"],
    customComponents: [],
  },
  INVOICE_PAYMENT_REPORT: {
    filters: ["report_type", "payment_dates", "clients"],
    customComponents: [],
  },
  BY_SERVICE_REPORT: {
    filters: [
      "report_type",
      "service_dates",
      "payment_status",
      "file_ids",
      "service_types",
      "clients",
      "invoice_statuses",
    ],
    customComponents: [],
  },
  BULK_BILLING_REPORT: {
    filters: ["report_type", "posted_dates", "clients", "invoice_statuses"],
    customComponents: [],
  },
  REVENUE_BY_CLIENT_REPORT: {
    filters: ["report_type", "posted_dates", "clients", "invoice_statuses"],
    customComponents: [],
  },
  NO_SERVICES_REPORT: {
    filters: [
      "report_type",
      "service_created_dates",
      "file_opened_dates",
      "clients",
    ],
    customComponents: [],
  },
  EMPTY_FILES_REPORT: {
    filters: [
      "report_type",
      "service_dates",
      "service_types",
      "invoice_statuses",
      "payment_status",
      "clients",
    ],
    customComponents: [],
  },
  AI_JOB_BILLING_REPORT: {
    filters: ["report_type", "service_dates"],
    customComponents: [],
  },
  SECTION_111_INVOICING: {
    filters: [
      "report_type",
      "s111_rre",
      "s111_file_type",
      "s111_send_dates",
      "s111_doi_dates",
      "s111_tpoc_dates",
      "s111_medicare_dates",
      "s111_hide_invoiced",
      "s111_action_filter",
      "s111_nsa_filter",
    ],
    customComponents: [],
  },
};

// filter category configurations with icons and labels
export const FILTER_CATEGORIES: Record<
  FilterCategory,
  { label: string; icon: string; hasSubOptions: boolean; required?: boolean }
> = {
  report_type: {
    label: "Report Type",
    icon: "description",
    hasSubOptions: true,
    required: true,
  },
  posted_dates: {
    label: "Posted Dates",
    icon: "calendar_today",
    hasSubOptions: true,
  },
  service_dates: {
    label: "Service Dates",
    icon: "event",
    hasSubOptions: true,
  },
  clients: {
    label: "Clients",
    icon: "business",
    hasSubOptions: true,
  },
  service_types: {
    label: "Service Types",
    icon: "category",
    hasSubOptions: true,
  },
  file_ids: {
    label: "File IDs",
    icon: "folder",
    hasSubOptions: true,
  },
  invoice_statuses: {
    label: "Invoice Status",
    icon: "receipt",
    hasSubOptions: true,
  },
  payment_status: {
    label: "Payment Status",
    icon: "payment",
    hasSubOptions: true,
  },
  invoice_numbers: {
    label: "Invoice Numbers",
    icon: "tag",
    hasSubOptions: true,
  },
  payment_dates: {
    label: "Payment Dates",
    icon: "payment",
    hasSubOptions: true,
  },
  service_created_dates: {
    label: "Service Created Dates",
    icon: "add_circle",
    hasSubOptions: true,
  },
  file_opened_dates: {
    label: "File Opened Dates",
    icon: "folder_open",
    hasSubOptions: true,
  },
  client_service_rules: {
    label: "Advanced Rules",
    icon: "rule",
    hasSubOptions: true,
  },
  icn: {
    label: "ICN",
    icon: "fingerprint",
    hasSubOptions: true,
  },
  claim_id: {
    label: "Claim ID",
    icon: "assignment",
    hasSubOptions: true,
  },
  claim_number: {
    label: "Claim Number",
    icon: "numbers",
    hasSubOptions: true,
  },
  rre_name: {
    label: "RRE Name",
    icon: "business",
    hasSubOptions: true,
  },
  rre_id: {
    label: "RRE ID",
    icon: "badge",
    hasSubOptions: true,
  },
  s111_claimant_name: {
    label: "S111 Claimant Name",
    icon: "person",
    hasSubOptions: true,
  },
  s111_rre: {
    label: "RRE",
    icon: "business",
    hasSubOptions: true,
  },
  s111_file_type: {
    label: "File Type",
    icon: "description",
    hasSubOptions: true,
  },
  s111_send_dates: {
    label: "Send Dates",
    icon: "send",
    hasSubOptions: true,
  },
  s111_hide_invoiced: {
    label: "Hide Invoiced",
    icon: "visibility_off",
    hasSubOptions: true,
  },
  s111_action_filter: {
    label: "Action",
    icon: "label",
    hasSubOptions: true,
  },
  s111_nsa_filter: {
    label: "NSA Code",
    icon: "code",
    hasSubOptions: true,
  },
  s111_doi_dates: {
    label: "DOI",
    icon: "calendar_today",
    hasSubOptions: true,
  },
  s111_tpoc_dates: {
    label: "TPOC Date",
    icon: "event",
    hasSubOptions: true,
  },
  s111_medicare_dates: {
    label: "Medicare Entitlement Date",
    icon: "favorite",
    hasSubOptions: true,
  },
};

/**
 * helper function to get available filters for a given report type
 */
export const getFiltersForReportType = (
  reportType: string | null,
): FilterCategory[] => {
  if (!reportType) return REPORT_CONFIGS.INVOICE_LIST.filters;

  const config = REPORT_CONFIGS[reportType as ReportType];
  return config ? config.filters : REPORT_CONFIGS.INVOICE_LIST.filters;
};

/**
 * helper function to check if a filter should be shown for current report type
 */
export const shouldShowFilter = (
  filterName: FilterCategory,
  reportType: string | null,
): boolean => {
  const filters = getFiltersForReportType(reportType);
  return filters.includes(filterName);
};
